import React from "react";
import { Card, Tag, Typography, Tooltip, Button } from "antd";
import { useTranslation } from "react-i18next";
import {
  EyeOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  PlayCircleFilled,
} from "@ant-design/icons";
import { aTextoPlano } from "../../../uiComponents/TextoClinico";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../revi/FilaTocable";
import { CardActionsMenu } from "../CardActionsMenu";
import "./SceneCard.scss";

const { Paragraph } = Typography;

export function SceneCard({ scene, onClick, onAdd, onInfo }) {
  // El titulo de la escena, sus etiquetas clinicas y su descripcion son DATO:
  // viven en el documento de escena y se traducen alli, no aqui.
  const { t } = useTranslation(["ui", "common", "rooms"]);
  const movil = useMovil();
  const { module_01, module_07 } = scene;

  const image =
    module_01?.bannertop?.url ||
    "https://via.placeholder.com/300x160?text=No+Image";

  const etiquetas = module_07?.ModSevenTherapyIds?.slice(0, 2) || [];

  /**
   * En movil la tarjeta deja de ser un bloque de 160 px de portada mas cuerpo
   * mas barra de tres botones -unos 320 px, media pantalla por sala- y pasa a
   * ser una fila con miniatura.
   *
   * No se pierde ninguna accion: tocar la fila abre la ficha, que es lo unico
   * reversible, y las otras dos viven en el menu. Lanzar queda a dos toques en
   * vez de a uno, que para algo que crea una sala VR de tres horas es lo
   * correcto.
   */
  if (movil) {
    return (
      <FilaTocable
        as={Card}
        alto={96}
        className="revi-scene-card revi-scene-card--fila"
        onTocar={() => onInfo(scene)}
        aria-label={module_01?.ModOneTitle}
        hoverable={false}
        kebab={
          <CardActionsMenu
            ariaLabel={module_01?.ModOneTitle}
            items={[
              {
                // Este boton NO consulta: crea una sala VR abierta tres horas y
                // abre otra pestana. Lleva el rotulo que le corresponde y
                // pregunta antes, igual que el del pie de la ficha. Sin esto
                // era la unica via del portal que la creaba de un toque.
                key: "lanzar",
                icon: <PlayCircleFilled />,
                label: t("rooms:detalleSala.lanzar"),
                run: () => onClick?.(scene),
                confirmacion: {
                  titulo: t("rooms:detalleSala.confirmarLanzarTitulo"),
                  consecuencia: t("rooms:detalleSala.confirmarLanzarCuerpo"),
                  textoOk: t("rooms:detalleSala.lanzar"),
                  peligroso: false,
                },
              },
              {
                key: "add",
                icon: <PlusCircleOutlined />,
                label: t("common:actions.add"),
                run: () => onAdd(scene),
              },
            ]}
          />
        }
      >
        <div className="revi-scene-card__fila">
          <img
            className="revi-scene-card__mini"
            alt=""
            src={image}
            loading="lazy"
          />

          <div className="revi-scene-card__filaTexto">
            <span className="revi-scene-card__filaTitulo">
              {module_01.ModOneTitle}
            </span>

            {etiquetas.length ? (
              <div className="revi-scene-card__filaTags">
                {etiquetas.map((tag) => (
                  <Tag
                    key={tag._id}
                    color={tag.colorCode}
                    variant="filled"
                    className="clinical-tag"
                  >
                    {tag.title}
                  </Tag>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </FilaTocable>
    );
  }

  return (
    <Card
      hoverable
      className="revi-scene-card"
      cover={
        <div
          className="card-cover-wrapper"
          onClick={() => onClick && onClick(scene)}
        >
          <img alt={module_01.ModOneTitle} src={image} loading="lazy" />
          <div className="hover-overlay">
            <PlayCircleFilled className="play-icon" />
          </div>
        </div>
      }
      // AQUI ESTÁN LOS 3 BOTONES SOLICITADOS
      actions={[
        <Tooltip title={t("escenas.ficha.verTooltip")}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => onClick(scene)}
            block
          >
            {t("common:actions.view")}
          </Button>
        </Tooltip>,
        <Tooltip title={t("escenas.ficha.infoTooltip")}>
          <Button
            type="text"
            icon={<InfoCircleOutlined />}
            onClick={() => onInfo(scene)}
            block
          >
            {t("escenas.ficha.info")}
          </Button>
        </Tooltip>,
        <Tooltip title={t("escenas.ficha.anadirTooltip")}>
          <Button
            type="text"
            icon={<PlusCircleOutlined />}
            onClick={() => onAdd(scene)}
            className="add-btn"
            block
          >
            {t("common:actions.add")}
          </Button>
        </Tooltip>,
      ]}
    >
      <div className="card-content" onClick={() => onClick && onClick(scene)}>
        {/* Título */}
        <h4 className="card-title" title={module_01.ModOneTitle}>
          {module_01.ModOneTitle}
        </h4>

        {/* Tags (Máximo 2) */}
        <div className="card-tags">
          {etiquetas.map((tag) => (
            <Tag
              key={tag._id}
              color={tag.colorCode}
              variant="filled" // Updated syntax
              className="clinical-tag"
            >
              {tag.title}
            </Tag>
          ))}
        </div>

        {/* Descripción: dos lineas de resumen, sin marcado ni etiquetas. */}
        <Paragraph className="card-desc" ellipsis={{ rows: 2 }}>
          {aTextoPlano(module_07?.ModSevenClinicalDescription) ||
            t("escenas.ficha.sinDescripcion")}
        </Paragraph>
      </div>
    </Card>
  );
}
